import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from "react-redux";
import FullCalendar from '@fullcalendar/react'
import dayGridPlugin from '@fullcalendar/daygrid'
import timeGridPlugin from '@fullcalendar/timegrid'
import interactionPlugin from '@fullcalendar/interaction'
import adaptivePlugin from '@fullcalendar/adaptive'
import resourceTimeGridPlugin from '@fullcalendar/resource-timegrid'
import moment from 'moment/moment'
import { Button, ToggleButton, OverlayTrigger, Tooltip  } from "react-bootstrap";
import { TIME_SLOTS, weekDays, COLOR_BY_STORAGE_KEY, BOARD_STORAGE_KEY } from "../../constants/variables";
import { CardsPullMemo } from "../FiltersSidebar";
import ClassCard from "../ClassCard";
import { ReactComponent as FilterIcon } from "../../assets/img/icons/filterList.svg";
import { ReactComponent as ScheduleIcon } from "../../assets/img/icons/schedule.svg";
import { DialogModal } from "../FilterDialogModal";
import { selectAll as selectAllBoards } from '../../redux/slices/boards.slice';
import { addChosenStudio, selectAll as selectAllStudio } from '../../redux/slices/studio.slice';
import Select, { components } from "react-select";
import "./index.scss"
import { useParams } from "react-router-dom";
import { addChosenSticker } from "../../redux/slices/stickers.slice";
import { modalEdit } from "../../redux/slices/modal.slice";
import styles from './index.module.scss';
import { addChosenBoard } from '../../redux/slices/boards.slice.js'
import { filterSetAll } from "../../redux/slices/scheduleFilters.slice";
import { getBackgroundColor } from "../../utilities";
import { resetHistory, resetNotificationHistory, eventsSetAll } from '../../redux/slices/events.slice';
import HistoryNotification from '../HistoryNotification';
import WeekDaysHeader from './WeekDaysHeader';
import { ReactComponent as ZoomInIcon } from "../../assets/img/icons/zoom-in.svg";
import { ReactComponent as ZoomOutIcon } from "../../assets/img/icons/zoom-out.svg";
import ExportPanel from "../ExportPanel";
import { useReactToPrint } from "react-to-print";
import WeekView from '../WeekView';
import ColorBySelect from '../ColorBySelect';
import ReportPanel from '../ReportPanel';
import { conflictCheckDays } from './conflictDays';

const Control = ({ children, ...props }) => {
    return (
        <components.Control {...props}>
            <ScheduleIcon className="ms-2" />
            {children}
        </components.Control>
    );
};

const createClassInstance = (data, colorBy = 'levels') => {
    const stickersArr = data.studios.map((item) => {
        return item.scheduleSticker
    }).flat();

    return stickersArr.map((item, index) => {
        return {
            id: item.id,
            resourceId: item.studioId,
            className: item.className,
            title: item.className || "None",
            length: item.length,
            start: moment(item.start, 'HH:mm:ss').format(),
            end: moment(item.start, 'HH:mm:ss').add(item.length, "hours").format(),
            background: getBackgroundColor(item, colorBy),
            styles: item.styles,
            levels: item.levels,
            teachers: item.teachers,
            teachersId: item.teachersId,
            levelsId: item.levelsId,
            students: item.students || [],
            studentIds: (item.students || []).map(s => s.id)
        }
    })
}

const getUserScopedKey = (userId) => `${BOARD_STORAGE_KEY}_${userId}`;

export default function ScheduleTable({board, setBoard, jumpTarget, onJumpHandled}) {
    const storedFilters = useSelector((state) => state.scheduleFilters.filters);
    const storedEvents = useSelector((state) => state.events.data);
    const eventsHistory = useSelector((state) => state.events.eventsHistory);

    const { isAdmin, id } = useSelector(state => state.personality.user);
    const { error, loadingErrors } = useSelector(state => state.events);
    const { loading } = useSelector((state) => state.boards);
    const allStudio = useSelector(selectAllStudio)
    const boards = useSelector(selectAllBoards);
    const params = useParams()
    const dispatch = useDispatch();

    const [currentEvents, setCurrentEvents] = useState([]);
    const { chosenBoard } = useSelector(state => state.boards);
    const [boardOptions, setBoardOptions] = useState([]);
    const calendarRef = useRef(null)
    const fcRef = useRef(null);
    const prevShowWeekViewRef = useRef(false);
    const eventsLastCheckedRef = useRef(null);

    const [filterModal, showFilterModal] = useState(false);
    const [colorBy, setColorBy] = useState(() => {
        const savedColorBy = localStorage.getItem(COLOR_BY_STORAGE_KEY);
        return savedColorBy || 'levels';
    });

    const studios = allStudio.map(item => ({ ...item, title: item.studioName }));

    const [weekday, setWeekday] = useState('monday');
    const [isOpenHistoryNotification, setIsOpenHistoryNotification] = useState(false)
    const [showWeekView, setShowWeekView] = useState(false);
    const [isOpenReport, setIsOpenReport] = useState(false);

    const activeFilters = useMemo(() => {
        let activeFilters = {};
        if (storedFilters) {
            for (let [key, value] of Object.entries(storedFilters)) {
                activeFilters[key] = value.filter((item) => item?.checked === true)
                if (activeFilters[key].length === 0) {
                    delete activeFilters[key]
                }
            }
        }

        return activeFilters
    }, [storedFilters])


    useEffect(() => {
        if (loading || boards.length === 0) return;

        const userId = isAdmin ? params.id : id;
        const storageKey = getUserScopedKey(userId);
        const savedBoardId = localStorage.getItem(storageKey);

        const savedBoard = boards.find(item => item.id === Number(savedBoardId));
        const reduxBoard = boards.find(item => item.id === chosenBoard?.id);
        const firstBoard = boards[0];

        const toOption = (option) => option ? ({
            value: option.id,
            label: option.boardName,
            ...option
        }) : null;

        const selectedBoard = toOption(savedBoard || reduxBoard || firstBoard);

        const boardsOptions = boards.map((item) => ({
            ...item,
            value: item.id,
            label: item.boardName
        }));
        
        setBoardOptions(boardsOptions);

        if (selectedBoard) {
            const shouldUpdateBoard = !board 
                || board.id !== selectedBoard.id 
                || board.boardDays !== selectedBoard.boardDays;
            const shouldUpdateChosenBoard = !chosenBoard 
                || chosenBoard.id !== selectedBoard.id 
                || chosenBoard.boardDays !== selectedBoard.boardDays;

            if (shouldUpdateBoard) {
                setBoard(selectedBoard);
            }
            if (shouldUpdateChosenBoard) {
                dispatch(addChosenBoard(selectedBoard));
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boards, loading, chosenBoard?.id, dispatch, id, params.id, isAdmin]);

    useEffect(() => {
        if (board?.boardDays) {
            for (const item of Object.keys(board.boardDays)) {
                if (!weekDays.includes(item)) continue;
                if (board.boardDays[item].end || board.boardDays[item].start) {
                    if (!weekday) setWeekday(item);
                    break;
                }
            }
        }


    }, [board, weekday]);

    useEffect(() => {
        dispatch(resetHistory());
        dispatch(resetNotificationHistory());
        setIsOpenHistoryNotification(false);
        if (board?.id) {
            dispatch({
                type: "STICKERS_GET_ALL",
                payload: {
                    userId: isAdmin ? params.id : id,
                    boardId: board?.id
                }
            })
        }


    }, [board?.id, dispatch, isAdmin, params.id, id]);

    useEffect(() => {
        if (weekday) dispatch(resetHistory());
    }, [dispatch, weekday]);

    useEffect(() => {
        if (!weekday || !board?.id || loading) return;
        
        dispatch({
            type: "EVENTS_SET_ALL",
            query: {
                dayOfTheWeek: weekday[0].toUpperCase() + weekday.slice(1),
                userId: isAdmin ? params.id : id,
                boardId: board.id
            }
        });
    }, [weekday, board, isAdmin, params.id, id, dispatch, loading]);

    // Reload events when returning from WeekView
    useEffect(() => {
        // Check if we're returning from WeekView (transition from true to false)
        const wasWeekViewOpen = prevShowWeekViewRef.current;
        const isReturningFromWeekView = wasWeekViewOpen && !showWeekView;
        
        if (isReturningFromWeekView && weekday && board?.id) {
            // Clear events immediately to avoid showing old cards
            dispatch(eventsSetAll(null));
            
            // Then load new events
            dispatch({
                type: "EVENTS_SET_ALL",
                query: {
                    dayOfTheWeek: weekday?.[0].toUpperCase() + weekday?.slice(1),
                    userId: isAdmin ? params.id : id,
                    boardId: board.value || boardOptions[0]?.id || board.id
                }
            })
        }
        
        // Update the ref for next render
        prevShowWeekViewRef.current = showWeekView;
    }, [showWeekView, weekday, board, dispatch, isAdmin, params.id, id, boardOptions]);


    useEffect(() => {
        let newEvents = [];
        if (storedEvents) {
            newEvents = createClassInstance(storedEvents, colorBy);
        }

        if (storedFilters && storedEvents) {
            const newEventsInstance = createClassInstance(storedEvents, colorBy);
            newEvents = newEventsInstance.filter((item) => {
                let flag = true
                for (let key of Object.keys(activeFilters)) {
                    if (key === "length") {
                        flag = activeFilters[key].find(el => el.value === item[key]);
                        return !!flag
                    } else {
                        flag = item[key] !== null && !!(activeFilters[key].find(el => item[key].includes(el.value)));
                    }
                    if (flag === false) return !!flag
                }
                return true
            });
        }
        setCurrentEvents(newEvents)
    }, [storedFilters, storedEvents, activeFilters, colorBy])

    const handleEventReceive = (eventInfo) => {
        if (isOpenHistoryNotification) return;
        const data = JSON.parse(eventInfo.draggedEl.getAttribute('data-event')) // receive data from sidebar class

        const body = {
            length: data.length,
            start: moment(eventInfo.date).format("HH:mm"),
            end: moment(eventInfo.date).add(data.length, "hours").format("HH:mm"),
            className: data.className,
            styles: data.styles,
            teachers: data.teachers,
            levels: data.levels,
            studioId: eventInfo.resource.id,
            dayOfTheWeek: weekday?.[0].toUpperCase() + weekday?.slice(1),
            userId: isAdmin ? params.id : id,
            teachersId: data.teachersId,
            levelsId: data.levelsId,
            boardId: board.id,
            stickerId: data.id,
            isReusable: data.isReusable,
            studentIds: data.studentIds || []
        }

        dispatch({
            type: "EVENTS_ADD_ONE",
            payload:
                { body, deleteSticker: !data.isReusable, sticker: data }
        })

        return false
    }

    const createEventBody = (event, oldEvent = null) => {
        return {
            scheduleId: event.id,
            id: event.id,
            length: event.extendedProps.length,
            className: event.extendedProps.className || event.title,
            start: moment(event.start).format("HH:mm"),
            end: moment(event.start).add(event.extendedProps.length, "hours").format("HH:mm"),
            styles: event.extendedProps.styles,
            levels: event.extendedProps.levels,
            teachers: event.extendedProps.teachers,
            teachersId: event.extendedProps.teachersId,
            levelsId: event.extendedProps.levelsId,
            students: event.extendedProps.students || [],
            studentIds: (event.extendedProps.students || []).map(s => s.id),
            studioId: event.getResources()[0].id,
            prevStudioId: oldEvent ? oldEvent.getResources()[0].id : event.getResources()[0].id,
            userId: isAdmin ? params.id : id,
            isReusable: false,
            dayOfTheWeek: weekday?.[0].toUpperCase() + weekday?.slice(1),
            boardId: board.id
        }
    }


    const handleEventClick = ({ event }) => {
        const item = createEventBody(event)
        dispatch(addChosenSticker(item));
        dispatch(modalEdit('Events'));
    }
    const showFilters = useCallback(() => {
        showFilterModal(!filterModal)
    }, [filterModal])

    const handleEvents = ({ event, oldEvent }) => {
        const body = createEventBody(event, oldEvent);
        let prevEvent = createEventBody(oldEvent);
        prevEvent = { ...prevEvent, studioId: oldEvent.getResources()[0].id, prevStudioId: event.getResources()[0].id }

        dispatch({ type: "EVENTS_UPDATE_ONE", payload: { body, prevEvent } })
    }

    const resourceLabels = (info) => {
        const editStudio = () => {
            dispatch(addChosenStudio({
                "id": info.resource.id,
                "studioName": info.resource.title,
                "boardId": board.value || board.id,
                "userId": isAdmin ? params.id : id
            }));

            dispatch(modalEdit('Studio'))
        }

        const addStudio = (i) => {
            const currentIndex = studios.findIndex(studio => {
                return studio.id === info.resource.id
            })
            let index = currentIndex + i;
            if (!i) index = currentIndex;
            dispatch(addChosenBoard({ ...board, indexNewStudio: index }));
            dispatch(modalEdit('Studio'));
        }


        return (
            <div className="resourceLabelWrapper">
                <Button className="text-decoration-none p-0 me-2" size="lg" variant="link"
                    onClick={() => addStudio()}>+</Button>
                <div onClick={editStudio}>{info.resource.title}</div>
                <Button className="text-decoration-none p-0 ms-2" size="lg" variant="link"
                    onClick={() => addStudio(+1)}>+</Button>
            </div>

        )
    }
    const isEventOverDiv = (x, y) => {
        const external_events = document.getElementById('events-pull');
        const domRect = external_events.getBoundingClientRect(); // get div coordinates
        return x >= domRect.left
            && y >= domRect.top
            && x <= domRect.right
            && y <= domRect.bottom;

    }
    const onEventDragStop = ({ event, jsEvent }) => {
        if (isOpenHistoryNotification) return;
        if (isEventOverDiv(jsEvent.clientX, jsEvent.clientY, jsEvent)) {
            const body = createEventBody(event);
            try {
                dispatch({
                    type: "EVENTS_REMOVE_ONE",
                    payload: { body, addSticker: true }
                })
                event.remove()
            }
            catch (e) {
                console.log(e);
            }
        }
    }

    const onFilterRemove = useCallback((filterItem) => {
        const updatedFilters = JSON.parse(JSON.stringify(storedFilters))
        updatedFilters[filterItem.entity] = updatedFilters[filterItem.entity].map((item) => {
            if (item.value === filterItem.value) {
                item.checked = false
            }
            return item
        })
        dispatch(filterSetAll(updatedFilters))
    }, [dispatch, storedFilters])
    
    const filtersList = () => {
        let filterList = Object.values(activeFilters).flat();
        return (
            <div className="d-flex flex-wrap align-items-center">
                <span className="me-2 mb-1 fw-bold fs-6">Filter ({filterList.length})</span>
                {filterList.map((item, index) => {
                    return (
                        <div key={`event-filters-${index}`} className="selectedOption">
                            <span className="text-capitalize me-1">{item.entity}:</span>
                            {item.label}
                            <div name={item.value} onClick={() => onFilterRemove(item)}
                                className="closeButton">X
                            </div>
                        </div>
                    )
                })}
            </div>
        )
    }



    const onUndoHandle = () => {
        if (eventsHistory.length === 0) return
        const { eventBody, reversedType, addSticker, deleteSticker, sticker } = eventsHistory[eventsHistory.length - 1]
        dispatch({
            type: reversedType,
            payload: {
                body: eventBody,
                isUndo: true,
                addSticker,
                deleteSticker,
                sticker,
            }
        })
    }

    const [slotTime, setSlotTime] = useState(TIME_SLOTS.MINUTES_15);
    const [highlightedStickerId, setHighlightedStickerId] = useState(null);
    const onChangeScale = useCallback((event) => {
        setSlotTime(event.currentTarget.value === TIME_SLOTS.MINUTES_15 ? TIME_SLOTS.MINUTES_5 : TIME_SLOTS.MINUTES_15);
    }, [])

    // Wrap ClassCard in a stable callback for FullCalendar's eventContent prop
    const renderClassCard = useCallback(
        (eventInfo) => ClassCard(eventInfo),
        []
    );

    // Check for conflicts reads the warnings once when it is opened. Classes
    // can still be moved, edited, added or deleted while it is open, so when the
    // classes on screen change after a save, the panel checks again instead of
    // showing what it found before the change.
    const checkConflicts = () => {
        eventsLastCheckedRef.current = storedEvents;
        dispatch({
            type: "GET_ALL_ERRORS",
            payload: {
                userId: isAdmin ? params.id : id,
                boardId: board.id,
                days: conflictCheckDays(board?.boardDays, weekday),
            }
        });
    };

    useEffect(() => {
        if (!isOpenHistoryNotification || !board?.id) return;
        if (storedEvents === eventsLastCheckedRef.current) return;
        checkConflicts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [storedEvents, isOpenHistoryNotification]);

        const onErrorClick = useCallback((sticker) => {
        if (!sticker) return;
        // Navigate to the correct day first
        const targetDay = (sticker.dayOfTheWeek || '').toLowerCase();
        if (targetDay) {
            setWeekday(targetDay);
        }
        // Highlight the card
        setHighlightedStickerId(String(sticker.id));
        // Scroll after day change re-renders (needs a small delay)
        setTimeout(() => {
            if (fcRef.current) {
                try {
                    fcRef.current.getApi().scrollToTime(sticker.start);
                } catch (e) {
                    // scroll failed silently
                }
            }
        }, 400);
        // Clear highlight after 3 seconds
        setTimeout(() => setHighlightedStickerId(null), 3000);
    }, [])

    // Handle cross-tab jump requests from Students tab
    useEffect(() => {
        if (!jumpTarget) return;
        onErrorClick(jumpTarget);
        if (onJumpHandled) onJumpHandled();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [jumpTarget]);

    const toggleWeekView = useCallback(() => {
        setShowWeekView(!showWeekView);
    }, [showWeekView])

    const handleColorByChange = (selectedOption) => {
        const newColorBy = selectedOption.value;
        setColorBy(newColorBy);
        localStorage.setItem(COLOR_BY_STORAGE_KEY, newColorBy);
    };

    // Export (day view PDF) — lifted from FiltersSidebar so button stays visible when conflicts open
    const handleExportPrint = useReactToPrint({
        documentTitle: `${board?.label || board?.boardName || 'schedule'}-${weekday}.pdf`.replace(' ', '_'),
        pageStyle: `@page { size: A4 landscape; margin: 8mm; } body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }`,
        onBeforeGetContent: () => new Promise((resolve) => {
            setSlotTime(TIME_SLOTS.MINUTES_15);
            resolve();
        }),
        content: () => {
            if (!calendarRef.current) return null;

            // Max studio columns that fit comfortably on A4 landscape.
            // Studios beyond this spill to page 2, 3, etc. — never clipped.
            const MAX_COLS_PER_PAGE = 4;

            // ── Shared CSS injected into every print document ──
            const makePrintStyle = () => {
                const el = document.createElement('style');
                el.textContent = `
                    .fc-non-business { display: none !important; }
                    .fc-timegrid-col-bg, .fc-bg-event {
                        background: transparent !important;
                        background-color: transparent !important;
                    }
                    .fc-view-harness-active { overflow: visible !important; }
                    .fc-timegrid-slot td {
                        border-top: none !important;
                        border-bottom: none !important;
                    }
                    .fc-timegrid-slot:not(.fc-timegrid-slot-minor) td {
                        border-top: 0.5px solid #d0d0d0 !important;
                    }
                    .fc-timegrid-slot-label-cushion {
                        font-weight: 700 !important;
                        font-size: 13px !important;
                    }
                    .fc-col-header-cell-cushion {
                        color: #2C2C2A !important;
                        font-weight: 700 !important;
                        text-decoration: none !important;
                        font-size: 13px !important;
                    }
                    /* ── Header bottom border (line under Studio names) ── */
                    .fc-col-header-cell {
                        border-bottom: 1px solid #d0d0d0 !important;
                    }
                    /* ── Grid bottom border (line after last hour) ── */
                    .fc-timegrid-slots table tbody tr:last-child td,
                    .fc-timegrid-cols table tbody tr:last-child td {
                        border-bottom: 0.5px solid #d0d0d0 !important;
                    }

                `;
                return el;
            };

            // ── Day-of-week label shown at top of each page ──
            const makeDayHeader = () => {
                const el = document.createElement('div');
                el.style.cssText = 'font-family: sans-serif; font-size: 22px; font-weight: 700; text-align: center; padding: 8px 0 4px; text-transform: capitalize;';
                el.textContent = weekday ? weekday.charAt(0).toUpperCase() + weekday.slice(1) : '';
                return el;
            };

            // ── DOM prep applied to every clone ──
            // Compensate for browser scale-down: browser shrinks print content to fit
            // A4 landscape. We read the live calendar width and set fonts proportionally
            // larger so they land at ~13px after the browser applies its scale factor.
            const containerWidth = calendarRef.current.offsetWidth || 1200;
            const A4_LANDSCAPE_PX = 1040; // A4 297mm - 16mm margins at 96dpi
            const browserScale = Math.min(1.0, A4_LANDSCAPE_PX / containerWidth);

            const prepareClone = (clone) => {
                const harness = clone.getElementsByClassName('fc-scroller-harness fc-scroller-harness-liquid')[0];
                if (harness) { harness.style.overflow = 'visible'; harness.style.borderTop = 'transparent'; }
                const scroller = clone.getElementsByClassName('fc-scroller fc-scroller-liquid-absolute')[0];
                if (scroller) scroller.style.overflow = 'visible';

                const tableTimeRows = clone.querySelectorAll('.fc-timegrid-slots table tbody tr');
                let startTimeDay = 0;
                if (tableTimeRows.length > 0) {
                    const firstCell = tableTimeRows[0].querySelector('td');
                    startTimeDay = firstCell ? parseInt(firstCell.getAttribute('data-time')) : 0;
                    for (let i = 0; i < tableTimeRows.length; i++) {
                        tableTimeRows[i].style.height = '5mm';
                    }
                }

                // Hour-only borders across all timegrid rows
                clone.querySelectorAll('.fc-timegrid-slot').forEach((row) => {
                    const isMajor = !row.classList.contains('fc-timegrid-slot-minor');
                    row.querySelectorAll('td').forEach((td) => {
                        td.style.setProperty('border-top', isMajor ? '0.5px solid #d0d0d0' : 'none', 'important');
                        td.style.setProperty('border-bottom', 'none', 'important');
                    });
                });

                // Border under studio name headers.
                // box-shadow bypasses border-collapse (which swallows border-bottom on table cells).
                clone.querySelectorAll('.fc-col-header-cell').forEach(el => {
                    el.style.setProperty('box-shadow', 'inset 0 -1px 0 0 #d0d0d0', 'important');
                });

                // Border at the very bottom of the grid (after the last time slot).
                // Target both the time-axis and the studio-column tables.
                clone.querySelectorAll('.fc-timegrid-slots table tbody tr:last-child td').forEach(td => {
                    td.style.setProperty('box-shadow', 'inset 0 -1px 0 0 #d0d0d0', 'important');
                });
                clone.querySelectorAll('.fc-timegrid-cols table tbody tr:last-child td').forEach(td => {
                    td.style.setProperty('box-shadow', 'inset 0 -1px 0 0 #d0d0d0', 'important');
                });

                // Sticker sizing / positioning
                const stickers = clone.getElementsByClassName('fc-timegrid-event-harness fc-timegrid-event-harness-inset');
                const DEFAULT_CARD_HEIGHT = 26;
                const STICKER_STYLE_TOP_RATIO = 6.45;
                for (let i = 0; i < stickers.length; i++) {
                    const sticker = stickers[i].getElementsByClassName('test')[0];
                    if (!sticker) continue;
                    const inner = sticker.querySelector('div');
                    if (inner) inner.style.overflow = 'hidden';
                    const length = parseFloat(sticker.getAttribute('data-length') || 0);
                    const timeStart = parseFloat(sticker.getAttribute('data-time') || startTimeDay);
                    // Font: target 13px on paper, scaled down only if card too short.
                    // Divide by browserScale so the font is larger pre-scale and lands
                    // at the right size after the browser shrinks content to fit the page.
                    // Select ALL card row divs (title, teacher, time).
                    // ClassCard structure: sticker > [aria-hidden overlays] + .card > [rows]
                    // The time row has no data-id so querySelectorAll('[data-id]') misses it.
                    // Skip aria-hidden overlays (tint + gradient strip) to reach the .card div,
                    // then grab its direct DIV children — all three rows.
                    const cardDiv = Array.from(sticker.children).find(
                        el => el.tagName === 'DIV' && !el.hasAttribute('aria-hidden')
                    );
                    const allCardItems = cardDiv
                        ? Array.from(cardDiv.children).filter(el => el.tagName === 'DIV')
                        : Array.from(sticker.querySelectorAll('[data-id="card-item"]'));
                    const numItems = Math.max(1, allCardItems.length);
                    const cardHeightMm = length * 20; // 4 slots/hr × 5mm/slot
                    const mmPerLine = 5.98; // 13px × 0.353mm/px × 1.3 lineHeight
                    const linesAvail = Math.max(0.5, (cardHeightMm - 2) / mmPerLine);
                    const targetPx = 13 * Math.min(1, linesAvail / numItems); // what we want on paper
                    const preFontSize = Math.min(18, Math.max(7.5, targetPx / browserScale));
                    const fontSize = `${preFontSize.toFixed(1)}px`;
                    const lineH = targetPx >= 11 ? '1.3' : '1.2';
                    allCardItems.forEach(el => {
                        el.style.setProperty('font-size', fontSize, 'important');
                        el.style.setProperty('line-height', lineH, 'important');
                    });
                    if (length >= 1.5) {
                        sticker.querySelectorAll('[data-id="card-item"]').forEach(el => {
                            el.style.overflow = 'auto'; el.style.display = 'block';
                            el.style.webkitLineClamp = 'initial'; el.style.webkitBoxOrient = 'horizontal';
                            el.style.wordBreak = 'normal';
                        });
                    }
                    stickers[i].style.height = `${length * DEFAULT_CARD_HEIGHT}mm`;
                    const countPoint = Math.abs(timeStart - startTimeDay) / 0.249;
                    stickers[i].style.top = `${countPoint !== 0 ? (STICKER_STYLE_TOP_RATIO + ((countPoint - 1) * STICKER_STYLE_TOP_RATIO)) : 0}mm`;
                    stickers[i].style.bottom = '0mm';
                }

                // Labels + grey cleanup
                // Time axis labels: keep at 13px — the column is narrow so larger sizes overflow.
                clone.querySelectorAll('.fc-timegrid-slot-label-cushion').forEach(el => {
                    el.style.setProperty('font-weight', '700', 'important');
                    el.style.setProperty('font-size', '13px', 'important');
                });
                // Studio name headers: scale-compensated so they land at 15px on paper.
                const studioNameSize = `${Math.min(22, (15 / browserScale)).toFixed(1)}px`;
                clone.querySelectorAll('.fc-col-header-cell-cushion').forEach(el => {
                    el.style.setProperty('font-size', studioNameSize, 'important');
                    el.style.setProperty('font-weight', '700', 'important');
                });
                clone.querySelectorAll('.fc-non-business').forEach(el => { el.style.display = 'none'; });
                clone.querySelectorAll('.fc-timegrid-col-bg').forEach(el => {
                    el.style.background = 'transparent'; el.style.backgroundColor = 'transparent';
                });
                clone.querySelectorAll('.fc-view-harness, .fc-timegrid-body').forEach(el => {
                    el.style.height = 'auto'; el.style.minHeight = '0';
                });

                // Width reset removed — manipulating FullCalendar's colgroups
                // breaks header/body column alignment. The browser's natural scaling
                // is acceptable; the font-size formula above already compensates.
            };

            // ── Determine resource (studio) column IDs in display order ──
            const probe = calendarRef.current.cloneNode(true);
            const resourceIds = Array.from(
                probe.querySelectorAll('.fc-col-header-cell[data-resource-id]')
            ).map(el => el.getAttribute('data-resource-id'));

            // ── Helper: hide columns not in the allowed set ──
            const applyColumnFilter = (clone, allowedIds) => {
                clone.querySelectorAll('.fc-col-header-cell[data-resource-id]').forEach(el => {
                    if (!allowedIds.has(el.getAttribute('data-resource-id'))) el.style.display = 'none';
                });
                clone.querySelectorAll('.fc-timegrid-col[data-resource-id]').forEach(el => {
                    if (!allowedIds.has(el.getAttribute('data-resource-id'))) el.style.display = 'none';
                });
            };

            // ── Single page (≤ MAX_COLS_PER_PAGE studios) ──
            if (resourceIds.length <= MAX_COLS_PER_PAGE) {
                const clone = calendarRef.current.cloneNode(true);
                prepareClone(clone);
                const wrapper = document.createElement('div');
                wrapper.appendChild(makePrintStyle());
                wrapper.appendChild(makeDayHeader());
                wrapper.appendChild(clone);
                return wrapper;
            }

            // ── Multi-page: split studios into groups of MAX_COLS_PER_PAGE ──
            const pageGroups = [];
            for (let i = 0; i < resourceIds.length; i += MAX_COLS_PER_PAGE) {
                pageGroups.push(new Set(resourceIds.slice(i, i + MAX_COLS_PER_PAGE)));
            }

            const container = document.createElement('div');
            container.appendChild(makePrintStyle());

            pageGroups.forEach((allowedIds, pageIndex) => {
                const pageClone = calendarRef.current.cloneNode(true);
                prepareClone(pageClone);
                applyColumnFilter(pageClone, allowedIds);

                const pageWrapper = document.createElement('div');
                if (pageIndex < pageGroups.length - 1) {
                    pageWrapper.style.pageBreakAfter = 'always';
                    pageWrapper.style.breakAfter = 'page';
                }
                pageWrapper.appendChild(makeDayHeader());
                pageWrapper.appendChild(pageClone);
                container.appendChild(pageWrapper);
            });

            return container;
        },
    });

    return (
        <div className="demo-app">
            {board ? (
                <>
                    {showWeekView ? (
                        <WeekView
                            board={board}
                            onClose={() => setShowWeekView(false)}
                            colorBy={colorBy}
                            onColorByChange={handleColorByChange}
                            onBoardChange={(value) => {
                                setBoard(value);
                                dispatch(addChosenBoard(value));
                                const userId = isAdmin ? params.id : id;
                                localStorage.setItem(getUserScopedKey(userId), value.id);
                            }}
                        />
                    ) : (
                        <>
                        <div className="app-main">
                            <div className="d-flex align-items-center position-relative flex-wrap" style={{ gap: '6px', marginTop: '8px', marginBottom: '6px' }}>
                                {/* Schedule selector — own flex child so it stays on row 1 alongside right-side buttons */}
                                <Select
                                    components={{ Control }}
                                    className="schedulesSelectButton my-1 mx-1"
                                    placeholder={"Schedules"}
                                    classNamePrefix="schedulesSelect"
                                    value={board}
                                    onChange={(value) => {
                                        setBoard(value);
                                        dispatch(addChosenBoard(value));
                                        const userId = isAdmin ? params.id : id;
                                        const storageKey = getUserScopedKey(userId);
                                        localStorage.setItem(storageKey, value.id);
                                    }}
                                    options={boardOptions}
                                    styles={{ container: (base) => ({ ...base, minWidth: '200px', width: 'auto', maxWidth: '280px', flexShrink: 0 }) }}
                                />
                                {/* Left group: Week View → Color by → Fine-tune → Filter */}
                                <div className={styles.buttonsLeft}>
                                    <button className={[styles.editButton, styles.weekViewButton].join(' ')} onClick={toggleWeekView}>
                                        <ScheduleIcon className="me-2" />
                                        Week View
                                    </button>
                                    <ColorBySelect propColorBy={colorBy} handleColorByChange={handleColorByChange}/>
                                    <OverlayTrigger placement="bottom" overlay={<Tooltip id="tooltip">Change snap precision for drag &amp; drop</Tooltip>}>
                                        <button className={[styles.editButton, styles.scaleButton].join(' ')} onClick={onChangeScale} value={slotTime}>
                                            {slotTime === TIME_SLOTS.MINUTES_15 ? <ZoomInIcon /> : <ZoomOutIcon />}
                                            <p>{slotTime === TIME_SLOTS.MINUTES_15 ? '5 min' : '15 min'}</p>
                                        </button>
                                    </OverlayTrigger>
                                    <div style={{ position: 'relative' }}>
                                        <ToggleButton
                                            className="filterButton greyButton my-1 mx-1"
                                            id="toggle-check"
                                            type="checkbox"
                                            variant="outline-primary"
                                            checked={filterModal}
                                            value="filterModal"
                                            onChange={showFilters}
                                        >
                                            <FilterIcon className="me-2" /> Filter
                                        </ToggleButton>
                                        <DialogModal isOpen={filterModal} onClose={showFilters} />
                                    </div>
                                </div>
                                {/* Right group: Report + Export (always visible) + Check/Close conflicts */}
                                <div className={styles.buttonsRight}>
                                    <button
                                        className={[styles.editButton, isOpenReport ? styles.reportButtonOpen : styles.reportButton].join(' ')}
                                        onClick={() => setIsOpenReport(o => !o)}
                                        title="Schedule report"
                                        aria-label="Schedule report"
                                    >
                                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                                            <line x1="18" y1="20" x2="18" y2="10"/><line x1="12" y1="20" x2="12" y2="4"/><line x1="6" y1="20" x2="6" y2="14"/>
                                        </svg>
                                        Report
                                    </button>
                                    <ExportPanel
                                        board={board}
                                        weekday={weekday}
                                        onPdfExport={handleExportPrint}
                                        userId={isAdmin ? params.id : id}
                                    />
                                    <button
                                        className={[styles.editButton, styles.conflictsButton, isOpenHistoryNotification ? styles.conflictsButtonOpen : ''].join(' ')}
                                        onClick={loadingErrors ? null : (() => {
                                            if (isOpenHistoryNotification) {
                                                setIsOpenHistoryNotification(false);
                                            } else {
                                                setIsOpenHistoryNotification(true);
                                                checkConflicts();
                                            }
                                        })}
                                    >
                                        {isOpenHistoryNotification ? '✕ Close conflicts' : '✓ Check for conflicts'}
                                    </button>
                                </div>
                            </div>
                            {Object.keys(activeFilters).length > 0 && filtersList()}
                            <WeekDaysHeader 
                                board={board} 
                                onUndoHandle={onUndoHandle} 
                                eventsHistory={eventsHistory} 
                                setWeekday={setWeekday} 
                                weekday={weekday} 
                            />
                            <div ref={calendarRef} className={studios.length > 3 ? styles.calendar32 : styles.calendar21}>
                                <FullCalendar
                                    ref={fcRef}
                                    aspectRatio={1.9}
                                    slotMinTime={storedEvents?.boardDays[weekday?.toLowerCase()]?.start || "06:00"}
                                    slotMaxTime={storedEvents?.boardDays[weekday?.toLowerCase()]?.end || "23:00"}
                                    schedulerLicenseKey={'CC-Attribution-NonCommercial-NoDerivatives'}
                                    events={currentEvents}
                                    resources={studios}
                                    resourceOrder={null}
                                    headerToolbar={false}
                                    plugins={[resourceTimeGridPlugin, timeGridPlugin, dayGridPlugin, interactionPlugin, adaptivePlugin]}
                                    initialView='resourceTimeGrid'
                                    resourceLabelContent={resourceLabels}
                                    defaultTimedEventDuration={'01:00'}
                                    editable={true}
                                    selectable={false}
                                    droppable={true}
                                    dragRevertDuration={0}
                                    slotDuration={slotTime}
                                    selectMirror={true}
                                    dayMaxEvents={true}
                                    allDaySlot={false}
                                    drop={handleEventReceive}
                                    slotLabelInterval={'01:00'}
                                    slotLabelFormat={{ hour: 'numeric', minute: '2-digit', meridiem: false }} // lower level of text
                                    eventTimeFormat={{
                                        hour: "numeric",
                                        minute: "2-digit",
                                        meridiem: false
                                    }}
                                    eventClassNames={({ event }) =>
                                        highlightedStickerId && String(event.id) === String(highlightedStickerId)
                                            ? ['fc-event-highlighted']
                                            : []
                                    }
                                    eventContent={renderClassCard}
                                    eventClick={handleEventClick}
                                    eventDurationEditable={false}
                                    eventOverlap={false}
                                    eventDragStop={onEventDragStop}
                                    eventChange={handleEvents}
                                        eventColor={'#f2f2f2'}
                                    eventReceive={(info) => {
                                        if (error /* === 'Not available to create more stickers in your subscription plan' */) {
                                            info.revert()
                                        }
                                    }}

                                />
                            </div>
                        </div>
                            {/* Class library sidebar — capped to viewport height, scrolls internally */}
                            <div className={styles.sidebarWrapper}>
                                {isOpenReport ? (
                                    <ReportPanel
                                        board={board}
                                        userId={isAdmin ? params.id : id}
                                        onClose={() => setIsOpenReport(false)}
                                    />
                                ) : isOpenHistoryNotification ? (
                                    <HistoryNotification setIsOpenHistoryNotification={setIsOpenHistoryNotification} onErrorClick={onErrorClick} />
                                ) : (
                                    <CardsPullMemo board={board} weekday={weekday} refProps={calendarRef} setSlotTime={setSlotTime} colorBy={colorBy}/>
                                )}
                            </div>
                        </>
                    )}
                    {/* {isOpenHistoryNotification ? 
                        (<HistoryNotification setIsOpenHistoryNotification={setIsOpenHistoryNotification} /> ):
                        (<CardsPullMemo board={board} weekday={weekday} refProps={calendarRef} setSlotTime={setSlotTime}/>)
                    } */}
                </>
            ) : (
                <h2 className="m-auto">
                    Click '+ New' to create your first schedule
                </h2>
            )}
        </div>
    )
}


